import { gql, useQuery } from '@apollo/client';
import { ApplicationStateProvider_GetCurrencies } from '@app/codegen';
import { useAppAuth } from '@itrustcapital/ui';
import appsettings from 'appsettings.json';
import React, { useMemo } from 'react';

export const APPLICATION_STATE_PROVIDER_GET_CURRENCIES = gql`
	query ApplicationStateProvider_GetCurrencies {
		currenciesV2(first: 100) {
			nodes {
				ticker
				name
				logoUrl
			}
			totalCount
		}
	}
`;

type ApplicationStateProviderStateContext = {
	currencies: {
		ticker: string;
		name: string;
		logoUrl: string;
	}[];
};

export type ApplicationStateProviderProps = {
	children?: React.ReactNode;
};

const ApplicationStateContext = React.createContext<ApplicationStateProviderStateContext>({
	currencies: [],
});

export function ApplicationStateProvider(props: ApplicationStateProviderProps) {
	const appAuth = useAppAuth(appsettings);

	const currenciesQuery = useQuery<ApplicationStateProvider_GetCurrencies>(
		APPLICATION_STATE_PROVIDER_GET_CURRENCIES,
		{
			skip: !appAuth.authState.isAuthenticated,
			fetchPolicy: 'cache-and-network',
		}
	);

	const currencies = useMemo(() => {
		return (
			currenciesQuery?.data?.currenciesV2?.nodes?.map((currency) => ({
				ticker: currency?.ticker || '',
				name: currency?.name || '',
				logoUrl: currency?.logoUrl || '',
			})) || []
		);
	}, [currenciesQuery?.data?.currenciesV2?.nodes]);

	return (
		<ApplicationStateContext.Provider
			value={{
				currencies,
			}}
		>
			{props?.children}
		</ApplicationStateContext.Provider>
	);
}

export function useApplicationStateContext() {
	const context = React.useContext(ApplicationStateContext);

	if (context === undefined) {
		throw new Error('useAppContext must be used within a AppProvider');
	}

	return context;
}
